// P/L 取込タブ用の CSV 解読ライブラリ（純関数のみ・ブラウザ File/ArrayBuffer から呼ぶ）。
// 対象は4種（plan.md §4-2 / §9-2）:
//   1. 楽天 月次収支見込み（RMS CSV・cp932・品目単位）           → POST /pl/import/rakuten-expense
//   2. 楽天 ポイント付与履歴（RMS CSV・cp932・注文単位）          → POST /pl/import/rakuten-points
//   3. 楽天 アフィリエイト パートナーレポート（UTF-8 BOM・合計行のみ送る） → POST /pl/import/rakuten-affiliate
//   4. 楽天 広告購入履歴（UTF-8 BOM・広告種別ごとの月額）         → POST /pl/import/rakuten-ad-purchase
//
// 文字コード判定: UTF-8 として decode → 置換文字 U+FFFD が出たら shift_jis で decode し直す。
// CSV: RFC4180風（ダブルクォート内カンマ・改行・""エスケープに対応）。

// ---------------------------------------------------------------------------
// 文字コード判定 + デコード
// ---------------------------------------------------------------------------

export type DetectedEncoding = "utf-8" | "utf-8-bom" | "shift_jis";

export interface DecodedText {
  text: string;
  encoding: DetectedEncoding;
}

/**
 * バイト列を UTF-8 として decode し、置換文字(U+FFFD)が出たら shift_jis で decode し直す。
 * UTF-8 BOM (EF BB BF) は 'utf-8-bom' として検出し、本文からは取り除く。
 */
export function decodeCsvBytes(buf: ArrayBuffer): DecodedText {
  const bytes = new Uint8Array(buf);
  const hasBom = bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf;
  const utf8Text = new TextDecoder("utf-8").decode(hasBom ? bytes.subarray(3) : bytes);
  if (!utf8Text.includes("�")) {
    return { text: utf8Text, encoding: hasBom ? "utf-8-bom" : "utf-8" };
  }
  const sjisText = new TextDecoder("shift_jis").decode(bytes);
  return { text: sjisText, encoding: "shift_jis" };
}

// ---------------------------------------------------------------------------
// RFC4180風 CSVパーサ（ダブルクォート内カンマ・改行・""エスケープ対応）
// ---------------------------------------------------------------------------

/** CSVテキスト全体を行×列の文字列配列にパースする。末尾の空行は無視する。 */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  const src = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (inQuotes) {
      if (c === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
      continue;
    }
    if (c === '"') {
      inQuotes = true;
      continue;
    }
    if (c === ",") {
      row.push(field);
      field = "";
      continue;
    }
    if (c === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
      continue;
    }
    field += c;
  }
  // 末尾フィールド
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  // 完全な空行（全フィールドが空文字）は末尾のみ落とす
  while (rows.length > 0) {
    const last = rows[rows.length - 1];
    if (last.every((f) => f.trim() === "")) rows.pop();
    else break;
  }
  return rows;
}

// ---------------------------------------------------------------------------
// 種別判定
// ---------------------------------------------------------------------------

export type CsvKind = "rakuten_expense" | "rakuten_points" | "rakuten_affiliate" | "rakuten_ad_purchase" | "unknown";

const EXPENSE_HEADER_MARK = "集約科目ID";
const POINTS_HEADER_MARK = "ポイント付与数";
const AFFILIATE_HEADER_MARK = "clicks,sales,amount,rewards";
const AD_PURCHASE_HEADER_MARK = "広告種別";

/** 全行(text丸ごと)からヘッダの目印文字列を探して種別を判定する。 */
export function detectCsvKind(text: string): CsvKind {
  if (text.includes(EXPENSE_HEADER_MARK)) return "rakuten_expense";
  if (text.includes(POINTS_HEADER_MARK)) return "rakuten_points";
  if (text.includes(AFFILIATE_HEADER_MARK)) return "rakuten_affiliate";
  if (text.includes(AD_PURCHASE_HEADER_MARK)) return "rakuten_ad_purchase";
  return "unknown";
}

// ---------------------------------------------------------------------------
// 数値・日付ユーティリティ
// ---------------------------------------------------------------------------

function toInt(s: string | undefined): number {
  if (!s) return 0;
  const n = Number(String(s).replace(/[,\s]/g, ""));
  return Number.isFinite(n) ? Math.round(n) : 0;
}

/** '202608' → '2026-08' */
function yyyymmToDash(s: string): string {
  const m = /^(\d{4})(\d{2})$/.exec(s.trim());
  return m ? `${m[1]}-${m[2]}` : s.trim();
}

/** 先頭の空行（CSVの前に改行だけの行が挟まるケース）を飛ばして最初の非空行のindexを返す。全行空なら -1。 */
function firstNonEmptyRowIndex(rows: string[][]): number {
  for (let r = 0; r < rows.length; r++) {
    if (!rows[r].every((f) => f.trim() === "")) return r;
  }
  return -1;
}

// ---------------------------------------------------------------------------
// 1. 楽天 月次収支見込み（rakuten_expense）
// ---------------------------------------------------------------------------

export interface RakutenExpenseRow {
  item_name: string;
  category_id?: number;
  status?: "概算" | "確定";
  amount: number;
  tax_rate?: number;
}

export interface RakutenExpenseParsed {
  kind: "rakuten_expense";
  month: string | null;
  rows: RakutenExpenseRow[];
  preview: { itemCount: number; totalAmount: number; statusCounts: Record<string, number> };
  warnings: string[];
}

/**
 * 列: 企業ID,企業名,店舗別ID,店舗名,店舗URL,対象年月,集約科目ID,集約科目名,品目名,概算/確定ステータス名,原価率,金額,消費税率区分名
 * ヘッダ有無を問わず列名で位置を特定する（列順が変わっても壊れにくい）。
 */
export function parseRakutenExpense(rows: string[][]): RakutenExpenseParsed {
  const warnings: string[] = [];
  const headerIdx = firstNonEmptyRowIndex(rows);
  if (headerIdx < 0) return { kind: "rakuten_expense", month: null, rows: [], preview: { itemCount: 0, totalAmount: 0, statusCounts: {} }, warnings: ["空のCSVです"] };
  const header = rows[headerIdx];
  const idx = (name: string) => header.indexOf(name);
  const iMonth = idx("対象年月");
  const iCategory = idx("集約科目ID");
  const iItem = idx("品目名");
  const iStatus = idx("概算/確定ステータス名");
  const iAmount = idx("金額");
  const iTax = idx("消費税率区分名");

  const out: RakutenExpenseRow[] = [];
  let month: string | null = null;
  const statusCounts: Record<string, number> = {};
  for (let r = headerIdx + 1; r < rows.length; r++) {
    const row = rows[r];
    if (row.every((f) => f.trim() === "")) continue;
    const itemName = iItem >= 0 ? row[iItem]?.trim() : "";
    if (!itemName) continue;
    const rawMonth = iMonth >= 0 ? row[iMonth]?.trim() : "";
    if (rawMonth && !month) month = yyyymmToDash(rawMonth);
    const status = iStatus >= 0 ? (row[iStatus]?.trim() as "概算" | "確定" | undefined) : undefined;
    if (status) statusCounts[status] = (statusCounts[status] ?? 0) + 1;
    const entry: RakutenExpenseRow = {
      item_name: itemName,
      amount: toInt(iAmount >= 0 ? row[iAmount] : undefined),
    };
    if (iCategory >= 0 && row[iCategory]?.trim()) entry.category_id = toInt(row[iCategory]);
    if (status === "概算" || status === "確定") entry.status = status;
    if (iTax >= 0 && row[iTax]?.trim() !== "") entry.tax_rate = toInt(row[iTax]);
    out.push(entry);
  }
  if (!month) warnings.push("対象年月が読み取れませんでした");
  const totalAmount = out.reduce((s, r) => s + r.amount, 0);
  return { kind: "rakuten_expense", month, rows: out, preview: { itemCount: out.length, totalAmount, statusCounts }, warnings };
}

// ---------------------------------------------------------------------------
// 2. 楽天 ポイント付与履歴（rakuten_points）
// ---------------------------------------------------------------------------

export interface RakutenPointsRow {
  order_number: string;
  purchase_date: string;
  points: number;
}

export interface RakutenPointsParsed {
  kind: "rakuten_points";
  rows: RakutenPointsRow[];
  preview: { count: number; totalPoints: number; byMonth: Record<string, { count: number; points: number }> };
  warnings: string[];
}

/** 列: 注文番号,購入日付,ポイント付与数 */
export function parseRakutenPoints(rows: string[][]): RakutenPointsParsed {
  const warnings: string[] = [];
  const headerIdx = firstNonEmptyRowIndex(rows);
  if (headerIdx < 0) return { kind: "rakuten_points", rows: [], preview: { count: 0, totalPoints: 0, byMonth: {} }, warnings: ["空のCSVです"] };
  const header = rows[headerIdx];
  const idx = (name: string) => header.indexOf(name);
  const iOrder = idx("注文番号");
  const iDate = idx("購入日付");
  const iPoints = idx("ポイント付与数");

  const out: RakutenPointsRow[] = [];
  const byMonth: Record<string, { count: number; points: number }> = {};
  for (let r = headerIdx + 1; r < rows.length; r++) {
    const row = rows[r];
    if (row.every((f) => f.trim() === "")) continue;
    const orderNumber = iOrder >= 0 ? row[iOrder]?.trim() : "";
    if (!orderNumber) continue;
    const purchaseDate = iDate >= 0 ? row[iDate]?.trim() : "";
    const points = toInt(iPoints >= 0 ? row[iPoints] : undefined);
    out.push({ order_number: orderNumber, purchase_date: purchaseDate, points });
    const month = purchaseDate.slice(0, 7);
    if (month) {
      byMonth[month] = byMonth[month] ?? { count: 0, points: 0 };
      byMonth[month].count += 1;
      byMonth[month].points += points;
    }
  }
  const totalPoints = out.reduce((s, r) => s + r.points, 0);
  return { kind: "rakuten_points", rows: out, preview: { count: out.length, totalPoints, byMonth }, warnings };
}

/** 5,000行ごとに分割（BFFのボディ上限対策・plan.md §4-3）。 */
export function chunkRakutenPoints(rows: RakutenPointsRow[], chunkSize = 5000): RakutenPointsRow[][] {
  if (rows.length <= chunkSize) return [rows];
  const chunks: RakutenPointsRow[][] = [];
  for (let i = 0; i < rows.length; i += chunkSize) chunks.push(rows.slice(i, i + chunkSize));
  return chunks;
}

// ---------------------------------------------------------------------------
// 3. 楽天 アフィリエイト パートナーレポート（rakuten_affiliate）
// ---------------------------------------------------------------------------

export interface RakutenAffiliateParsed {
  kind: "rakuten_affiliate";
  month: string | null;
  clicks: number;
  sales_count: number;
  sales_amount: number;
  rewards: number;
  partner_rows: number;
  warnings: string[];
}

/**
 * 1行目タイトル「パートナーレポート: 2026.09」・見出し行のあと英字ヘッダ clicks,sales,amount,rewards、
 * 以降パートナー行（先頭列は連番でindexなので無視）。合計行のみ body に載せる（パートナー行そのものは保持しない）。
 */
export function parseRakutenAffiliate(rows: string[][]): RakutenAffiliateParsed {
  const warnings: string[] = [];
  let month: string | null = null;
  let headerRowIdx = -1;
  let clicksColIdx = -1; // 英字ヘッダ行で 'clicks' が現れた列位置（先頭に連番用の空列が挟まることがあるため動的に探す）
  for (let r = 0; r < rows.length; r++) {
    const normalized = rows[r].map((f) => f.trim().toLowerCase());
    const i = normalized.findIndex(
      (v, idx) => v === "clicks" && normalized[idx + 1] === "sales" && normalized[idx + 2] === "amount" && normalized[idx + 3] === "rewards"
    );
    if (i >= 0) {
      headerRowIdx = r;
      clicksColIdx = i;
    }
    const titleMatch = /パートナーレポート[:：]\s*(\d{4})[.\-/](\d{1,2})/.exec(rows[r].join(","));
    if (titleMatch) month = `${titleMatch[1]}-${titleMatch[2].padStart(2, "0")}`;
  }
  if (headerRowIdx < 0) {
    warnings.push("英字ヘッダ(clicks,sales,amount,rewards)が見つかりませんでした");
    return { kind: "rakuten_affiliate", month, clicks: 0, sales_count: 0, sales_amount: 0, rewards: 0, partner_rows: 0, warnings };
  }
  if (!month) warnings.push("対象月（パートナーレポート: YYYY.MM）が読み取れませんでした");

  let clicks = 0;
  let salesCount = 0;
  let salesAmount = 0;
  let rewards = 0;
  let partnerRows = 0;
  let sawTotalRow = false;
  for (let r = headerRowIdx + 1; r < rows.length; r++) {
    const row = rows[r];
    if (row.every((f) => f.trim() === "")) continue;
    // 列: [...任意の前置列, clicks, sales, amount, rewards]（前置列数は clicksColIdx で判定）
    if (row.length < clicksColIdx + 4) continue;
    // 「合計」行が末尾に混ざるケース（review §4-5）: 前置列（パートナー連番のはずの列）に
    // 数値以外（「合計」等）が入っている行はパートナー行ではなく総計行なので二重計上を避けるため飛ばす。
    const prefixCells = row.slice(0, clicksColIdx);
    const isTotalRow = prefixCells.some((f) => /合計|total/i.test(f));
    if (isTotalRow) {
      sawTotalRow = true;
      continue;
    }
    clicks += toInt(row[clicksColIdx]);
    salesCount += toInt(row[clicksColIdx + 1]);
    salesAmount += toInt(row[clicksColIdx + 2]);
    rewards += toInt(row[clicksColIdx + 3]);
    partnerRows += 1;
  }
  if (sawTotalRow) warnings.push("「合計」行を検出したため集計から除外しました（二重計上防止）");
  return { kind: "rakuten_affiliate", month, clicks, sales_count: salesCount, sales_amount: salesAmount, rewards, partner_rows: partnerRows, warnings };
}

// ---------------------------------------------------------------------------
// 4. 楽天 広告購入履歴（rakuten_ad_purchase）
// ---------------------------------------------------------------------------

export interface RakutenAdPurchaseRow {
  ad_type_raw: string;
  item_name: string;
  amount: number;
  period_from?: string;
  period_to?: string;
  status?: string;
}

export interface RakutenAdPurchaseParsed {
  kind: "rakuten_ad_purchase";
  month: string | null;
  rows: RakutenAdPurchaseRow[];
  preview: { byType: Record<string, number>; total: number };
  warnings: string[];
}

/**
 * 列: 広告種別,お申し込みID,明細ID,広告商品名,枠名,レポート提供,デバイス,メディア,ステータス,
 *     掲載開始日,掲載終了日,購入額/暫定額,数量,購入日/提案日,同意日,同意状態
 * month はファイル名 `purchase_history_YYYY-MM~YYYY-MM.csv` の先頭月から取る（無ければ null＝ユーザーに選ばせる）。
 */
export function parseRakutenAdPurchase(rows: string[][], filename?: string): RakutenAdPurchaseParsed {
  const warnings: string[] = [];
  const headerIdx = firstNonEmptyRowIndex(rows);
  if (headerIdx < 0) return { kind: "rakuten_ad_purchase", month: null, rows: [], preview: { byType: {}, total: 0 }, warnings: ["空のCSVです"] };
  const header = rows[headerIdx];
  const idx = (name: string) => header.indexOf(name);
  const iType = idx("広告種別");
  const iItem = idx("広告商品名");
  const iAmount = idx("購入額/暫定額");
  const iFrom = idx("掲載開始日");
  const iTo = idx("掲載終了日");
  const iStatus = idx("ステータス");

  const out: RakutenAdPurchaseRow[] = [];
  const byType: Record<string, number> = {};
  for (let r = headerIdx + 1; r < rows.length; r++) {
    const row = rows[r];
    if (row.every((f) => f.trim() === "")) continue;
    const adType = iType >= 0 ? row[iType]?.trim() : "";
    if (!adType) continue;
    const amount = toInt(iAmount >= 0 ? row[iAmount] : undefined);
    const entry: RakutenAdPurchaseRow = {
      ad_type_raw: adType,
      item_name: iItem >= 0 ? row[iItem]?.trim() ?? "" : "",
      amount,
    };
    const from = iFrom >= 0 ? row[iFrom]?.trim() : "";
    const to = iTo >= 0 ? row[iTo]?.trim() : "";
    if (from) entry.period_from = from;
    if (to) entry.period_to = to;
    const status = iStatus >= 0 ? row[iStatus]?.trim() : "";
    if (status) entry.status = status;
    out.push(entry);
    byType[adType] = (byType[adType] ?? 0) + amount;
  }
  let month: string | null = null;
  if (filename) {
    const m = /purchase_history_(\d{4})-(\d{2})/.exec(filename);
    if (m) month = `${m[1]}-${m[2]}`;
  }
  if (!month) warnings.push("ファイル名から月を特定できませんでした（月を選択してください）");
  const total = out.reduce((s, r) => s + r.amount, 0);
  return { kind: "rakuten_ad_purchase", month, rows: out, preview: { byType, total }, warnings };
}

// ---------------------------------------------------------------------------
// 一括エントリポイント: ファイル(text)を判定してそれぞれの型で返す
// ---------------------------------------------------------------------------

export type ParsedCsv = RakutenExpenseParsed | RakutenPointsParsed | RakutenAffiliateParsed | RakutenAdPurchaseParsed;

export interface ParseFileResult {
  filename: string;
  encoding: DetectedEncoding;
  kind: CsvKind;
  parsed: ParsedCsv | null;
  warnings: string[];
}

/** File→ArrayBuffer→decode→parseCsv→種別判定→種別ごとのパーサ、までを1回で行う。 */
export async function parseCsvFile(file: File): Promise<ParseFileResult> {
  const buf = await file.arrayBuffer();
  const { text, encoding } = decodeCsvBytes(buf);
  return parseCsvText(text, file.name, encoding);
}

/** parseCsvFile のテスト用（Node等ブラウザ外から直接text/encodingを渡す版）。 */
export function parseCsvText(text: string, filename: string, encoding: DetectedEncoding): ParseFileResult {
  const kind = detectCsvKind(text);
  const rows = parseCsv(text);
  let parsed: ParsedCsv | null = null;
  switch (kind) {
    case "rakuten_expense":
      parsed = parseRakutenExpense(rows);
      break;
    case "rakuten_points":
      parsed = parseRakutenPoints(rows);
      break;
    case "rakuten_affiliate":
      parsed = parseRakutenAffiliate(rows);
      break;
    case "rakuten_ad_purchase":
      parsed = parseRakutenAdPurchase(rows, filename);
      break;
    case "unknown":
      parsed = null;
      break;
  }
  const warnings = parsed?.warnings ?? (kind === "unknown" ? ["ファイル種別を判定できませんでした（対応形式: 月次収支見込み／ポイント付与履歴／アフィリエイト／広告購入履歴）"] : []);
  return { filename, encoding, kind, parsed, warnings };
}
